import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { Box, Grid, TextField, IconButton, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import useApiCall from "../common/api-call";
import { getAssistantStatistics } from "./statistics-service";

const AssistantStatisticsView = () => {
  const { apiCall } = useApiCall();
  const [filteredData, setFilteredData] = useState([]);

  // Initialize dates
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  const fetchStatisticsData = async (start = startDate, end = endDate) => {
    try {
      const response = await getAssistantStatistics(apiCall, start, end);

      // Handle different possible response structures
      if (
        response &&
        response.statistics &&
        Array.isArray(response.statistics)
      ) {
        setFilteredData(response.statistics);
      } else if (response && Array.isArray(response)) {
        // Fallback for direct array response
        setFilteredData(response);
      } else if (response && response.data && Array.isArray(response.data)) {
        // Another possible structure
        setFilteredData(response.data);
      } else {
        console.log("Unexpected response structure:", response);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching assistant statistics:", error);
      setFilteredData([]);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const handleFetchData = () => {
    fetchStatisticsData(startDate, endDate);
  };

  // Calculate number of days between start and end date
  const calculateDaysBetween = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const timeDiff = endDateObj.getTime() - startDateObj.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
  };

  const daysBetween = calculateDaysBetween(startDate, endDate);

  const dayData = Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      day: date.toISOString().split("T")[0],
      requestCount: 0,
      quickCommandCount: 0,
      assistantCount: 0,
      errorCount: 0,
      tokenInputCount: 0,
      tokenOutputCount: 0,
    };
  });

  filteredData?.forEach(item => {
    // Use 'date' field from API response instead of 'time'
    const itemDate = item.date || "";
    const dateIndex = dayData.findIndex(day => day.day === itemDate);
    if (dateIndex !== -1) {
      dayData[dateIndex].requestCount += item.requestCount || 0;
      dayData[dateIndex].quickCommandCount += item.quickCommandCount || 0;
      dayData[dateIndex].assistantCount += item.assistantCount || 0;
      dayData[dateIndex].errorCount += item.errorCount || 0;
      dayData[dateIndex].tokenInputCount += item.tokenInputCount || 0;
      dayData[dateIndex].tokenOutputCount += item.tokenOutputCount || 0;
    }
  });

  const maxRequestCount = Math.max(
    ...dayData.map(item => item.requestCount),
    0,
  );
  const yAxisMax = maxRequestCount > 0 ? Math.ceil(maxRequestCount * 1.1) : 40;

  // Chart options for main statistics
  const getMainChartOption = () => ({
    title: {
      text: "Assistant Statistics",
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
      },
    },
    tooltip: {
      trigger: "axis",
      formatter: params => {
        const { name } = params[0];
        let tooltipContent = `Date: ${name}<br />`;
        params.forEach(param => {
          tooltipContent += `${param.seriesName}: ${param.value}<br />`;
        });
        return tooltipContent;
      },
    },
    legend: {
      data: [
        "Request Count",
        "Quick Command Count",
        "Assistant Count",
        "Error Count",
      ],
      top: "12%",
    },
    grid: {
      top: "30%",
      left: "15%",
      right: "10%",
      bottom: "15%",
    },
    xAxis: {
      type: "category",
      data: dayData.map(item => item.day),
      axisLabel: {
        interval: Math.max(1, Math.floor(daysBetween / 10)),
        rotate: 45,
        margin: 10,
        formatter: value => dayjs(value).format("MM-DD"),
        showMaxLabel: true,
      },
      axisTick: {
        alignWithLabel: true,
        interval: Math.max(1, Math.floor(daysBetween / 15)),
      },
    },
    yAxis: {
      type: "value",
      min: 0,
      max: yAxisMax,
      interval: Math.max(1, Math.ceil(yAxisMax / 10)),
      axisLabel: {
        formatter: value => Math.round(value).toLocaleString(),
        padding: [5, 5, 5, 5],
      },
      axisLine: {
        show: true,
      },
      axisTick: {
        show: true,
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: "dashed",
          color: "#ccc",
        },
      },
    },
    series: [
      {
        name: "Request Count",
        type: "line",
        data: dayData.map(item => item.requestCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#4caf50",
        },
      },
      {
        name: "Quick Command Count",
        type: "line",
        data: dayData.map(item => item.quickCommandCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#2196f3",
        },
      },
      {
        name: "Assistant Count",
        type: "line",
        data: dayData.map(item => item.assistantCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#ff9800",
        },
      },
      {
        name: "Error Count",
        type: "line",
        data: dayData.map(item => item.errorCount),
        animation: true,
        lineStyle: {
          width: 2,
        },
        itemStyle: {
          color: "#f44336",
        },
      },
    ],
  });

  // Chart options for token data
  const getTokenChartOption = () => {
    const maxTokenCount = Math.max(
      ...dayData.map(item =>
        Math.max(item.tokenInputCount, item.tokenOutputCount),
      ),
      0,
    );
    const tokenYAxisMax =
      maxTokenCount > 0 ? Math.ceil(maxTokenCount * 1.1) : 1000;

    return {
      title: {
        text: "Token Usage",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${
              param.seriesName
            }: ${param.value.toLocaleString()}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: ["Token Input", "Token Output"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: tokenYAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
          padding: [5, 5, 5, 5],
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: "Token Input",
          type: "line",
          data: dayData.map(item => item.tokenInputCount),
          animation: true,
          lineStyle: {
            width: 2,
          },
          itemStyle: {
            color: "#9c27b0",
          },
        },
        {
          name: "Token Output",
          type: "line",
          data: dayData.map(item => item.tokenOutputCount),
          animation: true,
          lineStyle: {
            width: 2,
          },
          itemStyle: {
            color: "#ff5722",
          },
        },
      ],
    };
  };

  // Chart options for error ratio
  const getErrorRatioChartOption = () => {
    const errorRatioData = dayData.map(item => {
      if (item.assistantCount === 0) return 0;
      return (item.errorCount / item.assistantCount) * 100;
    });

    const maxErrorRatio = Math.max(...errorRatioData, 0);
    const errorRatioYAxisMax =
      maxErrorRatio > 0 ? Math.ceil(maxErrorRatio * 1.1) : 10;

    return {
      title: {
        text: "Error Ratio (Error/Assistant %)",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name, value } = params[0];
          return `Date: ${name}<br />Error Ratio: ${value.toFixed(2)}%`;
        },
      },
      grid: {
        top: "25%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: errorRatioYAxisMax,
        axisLabel: {
          formatter: value => `${Math.round(value)}%`,
          padding: [5, 5, 5, 5],
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: "Error Ratio",
          type: "line",
          data: errorRatioData,
          animation: true,
          lineStyle: {
            width: 3,
          },
          itemStyle: {
            color: "#e91e63",
          },
          areaStyle: {
            opacity: 0.3,
            color: "#e91e63",
          },
        },
      ],
    };
  };

  useEffect(() => {
    // Initialize main chart
    const mainChartDom = document.getElementById("assistant-chart");
    if (mainChartDom) {
      const mainChart = echarts.init(mainChartDom);
      mainChart.setOption(getMainChartOption());

      // Initialize token chart
      const tokenChartDom = document.getElementById("token-chart");
      if (tokenChartDom) {
        const tokenChart = echarts.init(tokenChartDom);
        tokenChart.setOption(getTokenChartOption());

        // Initialize error ratio chart
        const errorRatioChartDom = document.getElementById("error-ratio-chart");
        if (errorRatioChartDom) {
          const errorRatioChart = echarts.init(errorRatioChartDom);
          errorRatioChart.setOption(getErrorRatioChartOption());

          const handleResize = () => {
            mainChart.resize();
            tokenChart.resize();
            errorRatioChart.resize();
          };

          window.addEventListener("resize", handleResize);

          return () => {
            mainChart.dispose();
            tokenChart.dispose();
            errorRatioChart.dispose();
            window.removeEventListener("resize", handleResize);
          };
        }
      }
    }
  }, [filteredData, startDate, endDate]);

  // Calculate totals for summary cards
  const totalRequestCount = dayData.reduce(
    (sum, item) => sum + item.requestCount,
    0,
  );
  const totalQuickCommandCount = dayData.reduce(
    (sum, item) => sum + item.quickCommandCount,
    0,
  );
  const totalAssistantCount = dayData.reduce(
    (sum, item) => sum + item.assistantCount,
    0,
  );
  const totalErrorCount = dayData.reduce(
    (sum, item) => sum + item.errorCount,
    0,
  );
  const totalTokenInputCount = dayData.reduce(
    (sum, item) => sum + item.tokenInputCount,
    0,
  );
  const totalTokenOutputCount = dayData.reduce(
    (sum, item) => sum + item.tokenOutputCount,
    0,
  );
  const errorRate =
    totalAssistantCount > 0
      ? ((totalErrorCount / totalAssistantCount) * 100).toFixed(1)
      : 0;

  return (
    <Box sx={{ flexGrow: 1, height: "100%", m: 2, pb: "80px" }}>
      {/* Date Selection Controls */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
          Date Range Selection
        </Typography>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
          >
            <IconButton
              onClick={handleFetchData}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                ml: 1,
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Total Requests
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#4caf50" }}>
            {totalRequestCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Assistant Count
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#ff9800" }}>
            {totalAssistantCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Error Count
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#f44336" }}>
            {totalErrorCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Error Rate
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#e91e63" }}>
            {errorRate}%
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Input Tokens
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#9c27b0" }}>
            {totalTokenInputCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Output Tokens
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#ff5722" }}>
            {totalTokenOutputCount.toLocaleString()}
          </Box>
        </Box>
      </Box>

      {/* Charts in Column Layout */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ width: "100%", height: { xs: "350px", sm: "450px" } }}>
            <Box
              id="assistant-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ width: "100%", height: { xs: "350px", sm: "450px" } }}>
            <Box id="token-chart" style={{ width: "100%", height: "100%" }} />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ width: "100%", height: { xs: "350px", sm: "450px" } }}>
            <Box
              id="error-ratio-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AssistantStatisticsView;
